import { useEffect, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Send, Loader2, Mail, Phone } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const plans = [
  { id: "tier1", label: "Tier 1 — Website Only · $150/mo" },
  { id: "tier2", label: "Tier 2 — Website + App · $300/mo" },
  { id: "tier3", label: "Tier 3 — Complex Builds · $500/mo · 24/7" },
  { id: "one-time", label: "One-Time Build — Own it outright" },
];

const empty = { name: "", email: "", company: "", business_type: "hospital", message: "" };

export default function SubscribeCTA({ selectedPlan, onPlanChange }) {
  const [form, setForm] = useState(empty);
  const [plan, setPlan] = useState(selectedPlan || "tier2");
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (selectedPlan) setPlan(selectedPlan);
  }, [selectedPlan]);

  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await axios.post(`${API}/leads`, { ...form, plan });
      toast.success("Request received. The Gsoftware team will contact you within 24 hours.");
      setForm(empty);
    } catch {
      toast.error("Transmission failed. Check your details and try again.");
    } finally {
      setLoading(false);
    }
  };

  const inputCls =
    "w-full border border-white/15 bg-black px-4 py-3.5 text-sm text-white placeholder-white/30 outline-none transition-colors duration-300 focus:border-[#00f0ff]";

  return (
    <section id="subscribe" className="noise-overlay circuit-bg relative border-t border-white/5 bg-[#050505] py-24 lg:py-32">
      <div
        className="pointer-events-none absolute left-1/2 top-0 h-72 w-[36rem] -translate-x-1/2 rounded-full opacity-15 blur-[120px]"
        style={{ background: "#00f0ff" }}
      />
      <div className="relative mx-auto grid max-w-7xl gap-14 px-6 lg:grid-cols-[0.9fr_1.1fr] lg:px-10">
        <div>
          <p className="font-mono-tech text-xs uppercase tracking-[0.3em] text-[#ff003c]">// Initialize</p>
          <h2 className="font-display mt-4 text-3xl font-black leading-tight tracking-tight sm:text-4xl">
            Plug your business into{" "}
            <span className="text-[#00f0ff] text-glow-cyan">Gsoftware</span>
          </h2>
          <p className="mt-6 max-w-md text-base leading-relaxed text-white/50 md:text-lg">
            Choose a subscription plan or request a one-time build. Tell us about your business
            and we'll respond within 24 hours with next steps.
          </p>
          <div className="mt-8 space-y-3">
            {plans.map((p) => (              <button
                key={p.id}
                type="button"
                onClick={() => { setPlan(p.id); onPlanChange(p.id); }}
                data-testid={`cta-plan-option-${p.id}`}
                className={`block w-full max-w-md border px-4 py-3 text-left font-mono-tech text-xs uppercase tracking-wider transition-colors duration-300 ${
                  plan === p.id
                    ? "border-[#00f0ff] bg-[#00f0ff]/10 text-[#00f0ff] glow-cyan"
                    : "border-white/15 text-white/50 hover:border-white/40 hover:text-white/80"
                }`}
              >
                {p.label}
              </button>
            ))}
          </div>
          <div className="mt-8 flex flex-wrap gap-x-8 gap-y-3">
            <a
              href="mailto:Damianoffice01@gmail.com"
              data-testid="cta-email-link"
              className="font-mono-tech flex items-center gap-2 text-xs uppercase tracking-widest text-white/50 transition-colors duration-300 hover:text-[#00f0ff]"
            >
              <Mail size={14} className="text-[#00f0ff]" /> Damianoffice01@gmail.com
            </a>
            <a
              href="tel:+17865643401"
              data-testid="cta-phone-link"
              className="font-mono-tech flex items-center gap-2 text-xs uppercase tracking-widest text-white/50 transition-colors duration-300 hover:text-[#00f0ff]"
            >
              <Phone size={14} className="text-[#ff003c]" /> (786) 564-3401
            </a>
          </div>
        </div>

        <form
          onSubmit={submit}
          data-testid="subscribe-form"
          className="border border-white/10 bg-black/60 p-8 backdrop-blur-md lg:p-10"
        >
          <div className="grid gap-5 sm:grid-cols-2">
            <div>
              <label htmlFor="lead-name" className="font-mono-tech mb-2 block text-[10px] uppercase tracking-widest text-white/40">
                Full Name *
              </label>
              <input
                id="lead-name"
                data-testid="lead-name-input"
                required
                value={form.name}
                onChange={set("name")}
                placeholder="Dr. Jane Smith"
                className={inputCls}
              />
            </div>
            <div>
              <label htmlFor="lead-email" className="font-mono-tech mb-2 block text-[10px] uppercase tracking-widest text-white/40">
                Email *
              </label>
              <input
                id="lead-email"
                type="email"
                data-testid="lead-email-input"
                required
                value={form.email}
                onChange={set("email")}
                placeholder="you@company.com"
                className={inputCls}
              />
            </div>
            <div>
              <label htmlFor="lead-company" className="font-mono-tech mb-2 block text-[10px] uppercase tracking-widest text-white/40">
                Business Name
              </label>
              <input
                id="lead-company"
                data-testid="lead-company-input"
                value={form.company}
                onChange={set("company")}
                placeholder="General Hospital / Bistro 51"
                className={inputCls}
              />
            </div>
            <div>
              <label htmlFor="lead-btype" className="font-mono-tech mb-2 block text-[10px] uppercase tracking-widest text-white/40">
                Business Type *
              </label>
              <select
                id="lead-btype"
                data-testid="lead-business-type-select"
                value={form.business_type}
                onChange={set("business_type")}
                className={inputCls}
              >
                <option value="hospital">Hospital / Clinic</option>
                <option value="restaurant">Restaurant / Hospitality</option>
                <option value="other">Other Business</option>
              </select>
            </div>
          </div>
          <div className="mt-5">
            <label htmlFor="lead-message" className="font-mono-tech mb-2 block text-[10px] uppercase tracking-widest text-white/40">
              What do you need built or automated?
            </label>
            <textarea
              id="lead-message"
              data-testid="lead-message-input"
              rows={4}
              value={form.message}
              onChange={set("message")}
              placeholder="Tell us about your goals — online ordering, patient intake, a full rebrand..."
              className={`${inputCls} resize-none`}
            />
          </div>
          <button
            type="submit"
            disabled={loading}
            data-testid="subscribe-submit-btn"
            className="glitch-hover mt-8 flex w-full items-center justify-center gap-2 border border-[#00f0ff] bg-[#00f0ff]/10 px-8 py-4 font-mono-tech text-sm font-bold uppercase tracking-widest text-[#00f0ff] transition-shadow duration-300 hover:glow-cyan disabled:opacity-50"
          >
            {loading ? <Loader2 size={16} className="animate-spin" /> : <Send size={16} />}
            {loading ? "Transmitting..." : `Start with ${plans.find((p) => p.id === plan)?.label.split("—")[0].trim()}`}
          </button>
          <p className="font-mono-tech mt-4 text-center text-[10px] uppercase tracking-widest text-white/30">
            No payment now — we scope it together first
          </p>
        </form>
      </div>
    </section>
  );
}
